import * as React from 'react';

import type { IconDefinition } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { FilesUpload } from './FilesUpload';
import { ImportModalFrame } from './ImportModalFrame';
import type { ImportModalProps, PendingFile } from './types';

/**
 * Prompt to import a file with specific instructions.
 */
export function FilesImportModal(
    props: ImportModalProps & {
        /** Type of file to upload */
        fileIcon: IconDefinition;
        fileExtension: string;

        /** Descriptions of instructions for this source. */
        steps?: React.ReactNode[];

        /** Show a warning about the maximum number of pages/files */
        limitWarning?: boolean;

        /** Limit the number of files that can be uploaded */
        fileUploadsLimit?: number;
    }
): React.ReactElement {
    const {
        getSpace,
        fileIcon,
        fileExtension,
        steps = [],
        startImport,
        importing,
        limitWarning = false,
        fileUploadsLimit,
    } = props;

    const [files, setFiles] = React.useState<PendingFile[]>([]);

    /** True if we are still uploading files. */
    const hasPendingUpload = files.some((file) => !file.uploaded);
    const nextDisabled = hasPendingUpload || files.length === 0;

    /** There's a delay to fetch the space during which we disable the modal actions. */
    const [submitting, setSubmitting] = React.useState(false);

    /** Run the import, it also closes the entire flow */
    const onSubmit = async () => {
        setSubmitting(true);
        if (!nextDisabled) {
            const space = await getSpace();
            startImport(
                files.map((file) => file.uploaded?.downloadURL).filter(filterOutNullable),
                space
            );
            setSubmitting(false);
        }
    };

    return (
        <ImportModalFrame
            {...props}
            onSubmit={onSubmit}
            cannotSubmit={nextDisabled}
            isLoading={submitting || importing}
        >
            {steps.length > 0 ? (
                <div className="mb-4 flex flex-col">
                    {steps.map((step, index) => (
                        <div key={index} className="flex gap-2">
                            <div>
                                <span className="text-muted">{`${index + 1}.`}</span>
                            </div>
                            <div className="flex-1">
                                <span className="text-muted">{step}</span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
            <FilesUpload
                getSpace={getSpace}
                files={files}
                setFiles={setFiles}
                fileIcon={fileIcon}
                fileExtension={fileExtension}
                limitWarning={limitWarning}
                fileUploadsLimit={fileUploadsLimit}
            />
        </ImportModalFrame>
    );
}
